/**
 * MODULE: useCustomerPortal.ts
 * PURPOSE: Open Stripe Customer Portal for payment method and subscription self-service
 */
import { useState, useCallback } from 'react';
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import { getErrorMessage } from '@shared/lib/frontend/api/errors';

const API = `${getApiBaseUrl()}/api/v1/auth/subscription`;

export function useCustomerPortal() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openPortal = useCallback(async (returnPath = '/settings/billing') => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const response = await fetch(`${API}/customer-portal-session`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ return_url: `${origin}${returnPath}` }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || body.message || 'Could not open billing portal');
      }
      const data = (await response.json()) as { url?: string };
      if (!data.url) {
        throw new Error('No portal URL returned');
      }
      window.location.href = data.url;
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Could not open billing portal'));
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return { openPortal, loading, error, clearError };
}
